/**
 * <hydrate-plp-products> — Layer 3 consumer custom element.
 *
 * Renders product cards built from the APIM product-search result produced by
 * the existing hydratePLP pipeline. Unlike a server-rendered Shopify product
 * grid, these cards come entirely from the NG API response (caption, image,
 * price, SKU) — no Liquid product loop is involved.
 *
 * Architecture position:
 *   Layer 1: query-product-list.js  (raw APIM fetch)
 *   Layer 2: hydrate-callers.js     (orchestration + `theme:search:hydrated` dispatch)
 *   Layer 3: hydrate-plp-products.js (this module — renders cards + pagination)
 *
 * Data source:
 *   - `window.__ngHydration` — the cached result, set by the auto-bootstrap in
 *     snippets/scripts.liquid once hydratePLP resolves (race-condition guard).
 *   - `theme:search:hydrated` CustomEvent on `document` — dispatched by
 *     hydrate-callers.js for live updates (re-fetches, filter changes).
 *
 * Pagination:
 *   The APIM response includes a `pagination: { page, totalPages }` object.
 *   When totalPages > 1, navigation controls are rendered into the
 *   `[data-hpp-pagination]` nav element using the shared hydrate-pagination
 *   module. Page changes dispatch a cancelable `theme:page:change` event so
 *   the filters section (when present) can own the re-fetch while preserving
 *   filter+sort state. When no filters section handles the event, this module
 *   falls back to a direct `hydratePLP` call.
 *
 * On a plain collection page with no `?q=` query the APIM call returns an error
 * envelope ("A search query string is required."). That is expected: outside of
 * the theme editor the section hides itself when there are no hits to show.
 */

import { renderPagination, bindPaginationEvents, readPaginationLabels, scrollToWithHeaderOffset } from '@theme/hydrate-pagination';
import { hydratePLP, FILTER_AGGREGATIONS } from '@theme/hydrate-callers';
import { renderProductCard, renderProductCardSkeletons, initCardInteractions } from '@theme/ng-product-card';
import { interleaveBanners } from '@theme/inline-banner';

/**
 * @typedef {Object} APIMHit
 * @property {string} [shopifyProductId]
 * @property {string} [shopifyProductHandle]
 * @property {string} [catalogSku]
 * @property {string} [storeName]
 * @property {{ caption?: string, image?: { url?: string }, priceInfo?: unknown }} [product]
 */

class HydratePlpProducts extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onHydrated = null;

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #totalPages = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {((event: Event) => void) | null} */
  #onViewChange = null;

  /** @type {((event: Event) => void) | null} */
  #onLoading = null;

  /** @type {((event: Event) => void) | null} */
  #onViewClick = null;

  connectedCallback() {
    this.maxProducts = parseInt(this.getAttribute('data-max-products') || '30', 10);

    // Shopify-backed manual collections render their cards in Liquid, but use
    // this element for the same persisted mobile grid/list behavior as API PLPs.
    // They must not subscribe to hydration events, which would replace the
    // server-rendered collection products with an API response.
    if (this.hasAttribute('data-liquid-fallback')) {
      this.#initViewToggle();
      this.#onViewClick = (event) => {
        const origin = event.target instanceof Element ? event.target : null;
        const button = origin?.closest('[data-hpp-view]');
        if (!button || !this.contains(button)) return;
        this.#setView(button.getAttribute('data-hpp-view') === 'list' ? 'list' : 'grid');
      };
      this.addEventListener('click', this.#onViewClick);
      return;
    }

    // Read initial page from URL so ?page=N shareable links work.
    this.#currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;

    this.#onHydrated = (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.#render(detail);
    };
    document.addEventListener('theme:search:hydrated', this.#onHydrated);

    // Paint skeletons for the whole in-flight window of every search that
    // targets this context — initial load and each filter / sort / page
    // re-fetch — until `theme:search:hydrated` swaps in the real cards.
    this.#onLoading = (event) => {
      if (/** @type {CustomEvent} */ (event).detail?.context === 'plp') this.#showLoading();
    };
    document.addEventListener('theme:search:loading', this.#onLoading);

    // Bind pagination click events (delegated listener on the nav element).
    bindPaginationEvents(
      this.querySelector('[data-hpp-pagination]'),
      (page) => this.#onPageChange(page)
    );

    // Mobile grid/list view toggle (controls mobile column count).
    this.#initViewToggle();

    // Race guard: if hydratePLP resolved before this element connected, the
    // event has already fired and been missed — process the cached result now.
    // Otherwise a fetch is expected on these templates: show the loading
    // skeletons until it resolves (the theme editor has no APIM data, so keep
    // the placeholder there instead).
    if (window.__ngHydration) {
      this.#render(window.__ngHydration);
    } else if (this.#isDesignMode) {
      this.#showEmptyState();
    } else {
      this.#showLoading();
    }
  }

  disconnectedCallback() {
    if (this.#onHydrated) {
      document.removeEventListener('theme:search:hydrated', this.#onHydrated);
    }

    if (this.#onLoading) {
      document.removeEventListener('theme:search:loading', this.#onLoading);
    }

    if (this.#onViewChange) {
      document.removeEventListener('theme:view:change', this.#onViewChange);
    }

    if (this.#onViewClick) {
      this.removeEventListener('click', this.#onViewClick);
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }
  }

  /**
   * Applies the persisted grid/list view on connect and keeps it in sync with
   * the mobile toggle that lives in the filter bar. The toggle dispatches a
   * `theme:view:change` event (and writes localStorage); this section just
   * reflects the chosen view on its grid container, which survives card
   * re-renders so it only needs setting on connect and on each change.
   */
  #initViewToggle() {
    this.#applyView(this.#storedView());

    this.#onViewChange = (event) => {
      const view = /** @type {CustomEvent} */ (event).detail?.view === 'list' ? 'list' : 'grid';
      this.#applyView(view);
    };
    document.addEventListener('theme:view:change', this.#onViewChange);
  }

  /** @returns {'grid' | 'list'} Persisted view preference, defaulting to grid. */
  #storedView() {
    try {
      return localStorage.getItem('ng:product-view') === 'list' ? 'list' : 'grid';
    } catch (error) {
      return 'grid';
    }
  }

  /**
   * Reflects a view on the grid container (CSS switches mobile columns).
   * @param {'grid' | 'list'} view
   */
  #applyView(view) {
    const grid = this.#grid;
    if (grid) grid.setAttribute('data-view', view);
    this.querySelectorAll('[data-hpp-view]').forEach((button) => {
      button.setAttribute('aria-pressed', button.getAttribute('data-hpp-view') === view ? 'true' : 'false');
    });
  }

  /** Persists and broadcasts a mobile card view selected in the fallback UI. */
  #setView(view) {
    try {
      localStorage.setItem('ng:product-view', view);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
    this.#applyView(view);
    document.dispatchEvent(new CustomEvent('theme:view:change', { detail: { view } }));
  }

  get #grid() {
    return this.querySelector('[data-hpp-grid]');
  }

  get #status() {
    return this.querySelector('[data-hpp-status]');
  }

  get #paginationNav() {
    return this.querySelector('[data-hpp-pagination]');
  }

  /** Whether the storefront is being viewed in the Shopify theme editor. */
  get #isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /** @returns {number} Number of products after which to insert a banner. */
  get #bannerInsertAfter() {
    return parseInt(this.getAttribute('data-banner-insert-after') || '0', 10) || 0;
  }

  /**
   * Parses the inline-banner JSON data embedded by the Liquid snippet.
   * @returns {import('@theme/inline-banner').InlineBanner[]}
   */
  get #bannerData() {
    const script = this.querySelector('script[data-inline-banners]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  /**
   * Renders product cards from a hydration result. Only acts on PLP-context
   * results; non-PLP contexts (typeahead/slp) are ignored.
   *
   * @param {{ context?: string, products?: { ok?: boolean, data?: { products?: APIMHit[], pagination?: { page?: number, totalPages?: number } }, error?: { message?: string } } } | null | undefined} result
   */
  #render(result) {
    if (!result || result.context !== 'plp') return;

    const envelope = result.products;

    if (!envelope || !envelope.ok) {
      this.#showEmptyState(envelope?.error?.message);
      return;
    }

    // Hits render as-is, in API order. A hit whose retail price is missing or
    // flagged `shouldDisplay: false` still renders — renderProductCard's
    // invalid-price gate shows it without a price.
    const hits = Array.isArray(envelope.data?.products) ? envelope.data.products : [];

    if (hits.length === 0) {
      this.#showEmptyState('No products to display.');
      return;
    }

    // Extract pagination metadata from the APIM response.
    const pagination = envelope.data?.pagination;
    if (pagination) {
      this.#currentPage = pagination.page || 1;
      this.#totalPages = pagination.totalPages || 1;
    }

    const grid = this.#grid;
    if (!grid) return;

    // Render ALL hits from the response — the API returns the correct
    // page-sized batch; no client-side slicing needed.
    const allowAtc = this.hasAttribute('data-allow-atc');
    const cardHtmlArray = hits.map((hit) => renderProductCard(hit, { allowAtc }));

    const banners = this.#bannerData;
    const insertAfter = this.#bannerInsertAfter;

    grid.innerHTML = (banners.length > 0 && insertAfter > 0)
      ? interleaveBanners(cardHtmlArray, banners, insertAfter)
      : cardHtmlArray.join('');

    // Attach delegated card interaction handlers (wishlist, swatches, sizes).
    initCardInteractions(grid);

    // Clear the loading state now that real cards are in place.
    grid.removeAttribute('aria-busy');

    const status = this.#status;
    if (status) status.hidden = true;

    // Render pagination controls.
    const paginationNav = this.#paginationNav;
    if (paginationNav) {
      paginationNav.innerHTML = renderPagination(
        this.#currentPage,
        this.#totalPages,
        readPaginationLabels(paginationNav)
      );
      paginationNav.hidden = this.#totalPages <= 1;
    }

    this.hidden = false;
  }

  /**
   * Handles a pagination page-change request. Updates the URL, dispatches
   * a cancelable `theme:page:change` event (so the filters section can own
   * the re-fetch when present), and falls back to a direct hydratePLP call
   * when no other handler prevents the default.
   *
   * @param {number} page - 1-based target page number.
   */
  async #onPageChange(page) {
    this.#currentPage = page;

    // Update URL for shareability — remove `page` param when on page 1.
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    history.replaceState(null, '', url.toString());

    // Dispatch cancelable event — the filters section calls preventDefault()
    // when it handles the re-fetch (preserving filter + sort state).
    const event = new CustomEvent('theme:page:change', {
      detail: { page, context: 'plp' },
      cancelable: true,
      bubbles: true,
    });
    document.dispatchEvent(event);

    if (!event.defaultPrevented) {
      // No filters section on the page — do a direct re-fetch.
      if (this.#abortController) {
        this.#abortController.abort();
      }
      this.#abortController = new AbortController();

      const query = new URLSearchParams(window.location.search).get('q') || '';
      const hasFilters = Boolean(document.querySelector('hydrate-filters'));
      const productParams = hasFilters ? { aggregations: FILTER_AGGREGATIONS } : {};

      try {
        const result = await hydratePLP({
          query,
          page,
          productSize: this.maxProducts,
          productParams,
          // No store module renders in the 'plp' context — skip store-search.
          skipStores: true,
          signal: this.#abortController.signal,
        });
        window.__ngHydration = result;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('[plp-products] page change failed', error);
      }
    }

    // Scroll to the top of this section after page change, offsetting the
    // sticky header so the first product isn't hidden behind it.
    scrollToWithHeaderOffset(this);
  }

  /**
   * Hides the whole section on the live storefront when there is nothing to
   * show. In the theme editor the section stays visible with a placeholder so
   * merchants can find and position it.
   *
   * @param {string} [message]
   */
  #showEmptyState(message) {
    if (this.#isDesignMode) {
      this.hidden = false;
      const status = this.#status;
      if (status) {
        status.hidden = false;
        status.textContent = message || 'Product results will appear here when search data is available.';
      }
      return;
    }

    this.hidden = true;
  }

  /**
   * Paints skeleton cards into the grid while a search is in flight, keeping
   * the section visible and marking the grid `aria-busy`. Replaced by real
   * cards (or hidden) once `theme:search:hydrated` resolves.
   */
  #showLoading() {
    const grid = this.#grid;
    if (!grid) return;

    this.hidden = false;

    const status = this.#status;
    if (status) status.hidden = true;

    const paginationNav = this.#paginationNav;
    if (paginationNav) paginationNav.hidden = true;

    // Cap the placeholder count so a large page size doesn't paint dozens of
    // skeletons; one screenful is enough to signal loading.
    const count = Math.min(this.maxProducts || 8, 12);
    grid.setAttribute('aria-busy', 'true');
    grid.innerHTML = renderProductCardSkeletons(count);
  }

}

if (!customElements.get('hydrate-plp-products')) {
  customElements.define('hydrate-plp-products', HydratePlpProducts);
}
